import { ApolloError } from "@apollo/client";
import { Button, Grid, Paper, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ErrorBoxDialog } from "components/error";
import { RestrictedTenantRealm, TenantPermission } from "features/tenant";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { createRoute } from "utils/url";
import * as Yup from "yup";
import TokenDisplayPopup from "./TokenDisplayPopup";
import {
  useCreateGrant,
  useCreateGrantWithApiKeyValue
} from "./useAccessGrants";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    border: "solid 1px #dcdcdc",
    width: "100%"
  },
  title: {
    color: "#1e1e1e",
    fontSize: "14.4px",
    fill: "#464646",
    font: "inherit",
    margin: 0,
    letterSpacing: 0,
    fontWeight: 600
  },
  grid: {
    marginBottom: theme.spacing(2)
  },
  label: {
    color: "#464646",
    fill: "#464646",
    fontSize: "12px",
    font: "inherit",
    fontWeight: 600
  },
  input: {
    fill: "#464646",
    font: "inherit",
    outline: "none",
    flex: "auto",
    display: "block",
    wordBreak: "break-word",
    width: "100%",
    maxWidth: "100%",
    fontSize: "1rem",
    borderRadius: 2,
    lineHeight: 1.32,
    color: "#464646",
    border: "solid 1px #8c8c8c",
    padding: "5px"
  },
  rootInput: {
    backgroundColor: "#fff",
    margin: 0,
    padding: 0,
    border: "0px"
  },
  helperText: {
    fontSize: "0.75rem",
    color: "#6e6e6e",
    marginTop: theme.spacing(0.5)
  },
  disabledButton: {
    backgroundColor: "#C7CFD4 !important",
    color: "#68777B !important"
  },
  createButton: {
    textTransform: "none",
    color: "white"
  },
  cancelButton: {
    color: "black",
    borderColor: "#bdbdbd",
    textTransform: "none"
  }
}));

const notNullOrEmpty = (value?: string | null) =>
  value != null && !isEmpty(value.trim());

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .test(
      "not-empty-or-whitespace",
      "Name must not be empty or whitespace",
      notNullOrEmpty
    ),
  apiKeyInput: Yup.string()
    .required("API Key ID or API Key value is required")
    .test(
      "not-empty-or-whitespace",
      "API Key ID or API Key value must not be empty or whitespace",
      notNullOrEmpty
    )
});

interface CreateGrantFormValues {
  name: string;
  apiKeyInput: string;
}

const AccessGrantCreate: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [token, setToken] = useState<string>("");
  const [apiKeyError, setApiKeyError] = useState<string>("");

  const [
    createGrantById,
    { error: createByIdError, reset: resetCreateByIdError }
  ] = useCreateGrant();

  const [
    createGrantByValue,
    { error: createByValueError, reset: resetCreateByValueError }
  ] = useCreateGrantWithApiKeyValue();

  const isApiKeyId = (input: string): boolean => {
    return input.length <= 20;
  };

  const handleApiKeyError = (error: ApolloError) => {
    if (error.message.toLowerCase().includes("api key")) {
      setApiKeyError("Invalid API Key");
    }
  };

  const handleSubmit = async (
    values: CreateGrantFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setApiKeyError("");
    try {
      if (isApiKeyId(values.apiKeyInput)) {
        await createGrantById({
          variables: {
            name: values.name,
            apiKeyId: values.apiKeyInput
          },
          onCompleted: data => {
            setToken(data.grants.createGrant?.token ?? "");
          },
          onError: handleApiKeyError
        });
      } else {
        await createGrantByValue({
          variables: {
            name: values.name,
            apiKeyValue: values.apiKeyInput
          },
          onCompleted: data => {
            setToken(data.grants.createGrantWithApiKeyValue?.token ?? "");
          },
          onError: handleApiKeyError
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseToken = () => {
    setToken("");
    history.push(createRoute("/api/access-grants"));
  };

  const error = createByIdError || createByValueError;

  return (
    <RestrictedTenantRealm
      requiredPermissions={[TenantPermission.ManageAccessGrants]}
    >
      <Formik
        initialValues={{ name: "", apiKeyInput: "" }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, touched, errors }) => (
          <Form>
            <Grid container justifyContent="center">
              <Grid item xs={12} md={8} lg={6}>
                <Paper elevation={1}>
                  <Grid container className={classes.container}>
                    <Grid container item xs={12} className={classes.grid}>
                      <Grid item xs={12}>
                        <label className={classes.label}>Name *</label>
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          name="name"
                          variant="standard"
                          fullWidth
                          placeholder="Enter grant Name"
                          InputProps={{
                            classes: {
                              root: classes.rootInput,
                              input: classes.input
                            },
                            disableUnderline: true
                          }}
                          inputProps={{ "data-testid": "grant-name-input" }}
                          error={touched.name && Boolean(errors.name)}
                          helperText={<ErrorMessage name="name" />}
                        />
                        <div className={classes.helperText}>
                          A descriptive name for this access grant.
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} className={classes.grid}>
                      <Grid item xs={12}>
                        <label className={classes.label}>
                          API Key ID or API Key Value *
                        </label>
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          name="apiKeyInput"
                          variant="standard"
                          fullWidth
                          placeholder="Enter API Key ID or full API Key Value"
                          InputProps={{
                            classes: {
                              root: classes.rootInput,
                              input: classes.input
                            },
                            disableUnderline: true
                          }}
                          inputProps={{ "data-testid": "api-key-input" }}
                          error={
                            (touched.apiKeyInput &&
                              Boolean(errors.apiKeyInput)) ||
                            Boolean(apiKeyError)
                          }
                          helperText={
                            apiKeyError ? (
                              <span style={{ color: "#d32f2f" }}>
                                {apiKeyError}
                              </span>
                            ) : (
                              <ErrorMessage name="apiKeyInput" />
                            )
                          }
                        />
                        <div className={classes.helperText}>
                          Enter either API Key ID (e.g., "my-api-key-123") or
                          the full API Key Value
                        </div>
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      item
                      xs={12}
                      spacing={2}
                      justifyContent="flex-end"
                    >
                      <Grid item>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={isSubmitting}
                          className={
                            isSubmitting
                              ? classes.disabledButton
                              : classes.createButton
                          }
                          data-testid="create-grant-button"
                        >
                          Create Grant
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={() =>
                            history.push(createRoute("/api/access-grants"))
                          }
                          className={classes.cancelButton}
                          data-testid="cancel-create-button"
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
      <TokenDisplayPopup
        open={!!token}
        token={token}
        onClose={handleCloseToken}
      />
      <ErrorBoxDialog
        apolloError={apiKeyError ? null : error}
        onClose={() => {
          if (createByIdError) resetCreateByIdError();
          if (createByValueError) resetCreateByValueError();
          setApiKeyError("");
        }}
      />
    </RestrictedTenantRealm>
  );
};

export default AccessGrantCreate;
